// src/app/features/trade/tradeApi.js
import http from "../../api/http.js";

/**
 * Remove react-query ctx junk and non-primitive params (AbortSignal, QueryClient, objects)
 * so you don't end up with:
 *   client=[object Object]&signal=[object AbortSignal]
 */
function sanitizeParams(params = {}) {
  if (!params || typeof params !== "object") return {};

  const out = {};

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;

    // Drop react-query context keys if someone passes the ctx object
    if (k === "client" || k === "queryKey" || k === "signal" || k === "meta") {
      continue;
    }

    // Only allow primitives + arrays of primitives
    const t = typeof v;

    if (t === "string" || t === "number" || t === "boolean") {
      out[k] = v;
      continue;
    }

    if (Array.isArray(v)) {
      const filtered = v.filter((x) => {
        const xt = typeof x;
        return xt === "string" || xt === "number" || xt === "boolean";
      });
      if (filtered.length) out[k] = filtered;
      continue;
    }
  }

  return out;
}

/**
 * Clamp limit to prevent 422s when backend has strict Query(le=...) validations.
 */
const MAX_LIMIT = 100;
function clampLimit(n, max = MAX_LIMIT) {
  const x = Number(n);
  if (!Number.isFinite(x)) return Math.min(10, max);
  return Math.max(1, Math.min(x, max));
}

function errorMessage(error, fallback = "Request failed") {
  // Prefer normalized message from http.js interceptor
  return (
    error?.userMessage ||
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

/**
 * ✅ IMPORTANT:
 * Backend expects snake_case + requires funded_asset and funded_network.
 * Your UI currently sends camelCase (stakeAmount, durationSeconds, payoutMultiplier).
 * This mapper ensures we ALWAYS send the backend-compatible shape.
 */
// In tradeApi.js - add this to normalizeTradeCreatePayload
function normalizeTradeCreatePayload(input = {}) {
  console.log("🔍 Input payload:", input);
  
  const src = input && typeof input === "object" ? input : {};

  const stakeAmount =
    src.stake_amount ??
    src.stakeAmount ??
    src.stake ??
    src.amount ??
    undefined;

  const durationSeconds =
    src.duration_seconds ??
    src.durationSeconds ??
    src.duration ??
    undefined;

  const payoutMultiplier =
    src.payout_multiplier ??
    src.payoutMultiplier ??
    src.multiplier ??
    undefined;

  const fundedAsset =
    src.funded_asset ??
    src.fundedAsset ??
    "USDT";

  const fundedNetwork =
    src.funded_network ??
    src.fundedNetwork ??
    "TRON";

  const payload = {
    market: src.market,
    direction: src.direction,
    stake_amount: stakeAmount,
    duration_seconds: durationSeconds,
    payout_multiplier: payoutMultiplier,
    funded_asset: fundedAsset,
    funded_network: fundedNetwork,
  };

  // remove undefined keys (but keep 0 if ever used)
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  console.log("📤 Normalized payload:", payload);
  return payload;
}
/**
 * Create a new trade request
 * Backend: POST /trade-requests
 */
export async function createTradeRequest(tradeData) {
  try {
    const payload = normalizeTradeCreatePayload(tradeData);
    const res = await http.post("/trade-requests", payload);
    return res.data;
  } catch (error) {
    console.error("Error creating trade request:", error);

    if (error.response?.status === 400) throw new Error("Invalid trade request data");
    if (error.response?.status === 403) throw new Error("Insufficient balance for this trade");

    if (error.response?.status === 422) {
      // If FastAPI returns validation details, surface missing fields clearly
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const fields = detail
          .map((d) => (Array.isArray(d?.loc) ? d.loc.join(".") : null))
          .filter(Boolean);
        if (fields.length) {
          throw new Error(`Trade validation failed: ${fields.join(", ")}`);
        }
      }
      throw new Error("Trade validation failed. Please check your inputs.");
    }

    throw new Error(`Failed to create trade: ${errorMessage(error)}`);
  }
}

/**
 * Get trade requests (pending/approved/rejected moderation states)
 * Backend: GET /trade-requests
 * Expected: { items, total, limit, offset }
 */
export async function getTradeRequests(params = {}) {
  try {
    const cleanParams = sanitizeParams({
      status: params.status || undefined,
      limit: clampLimit(params.limit ?? 10, MAX_LIMIT),
      offset: params.offset ?? 0,
      market: params.market || undefined,
      direction: params.direction || undefined,
    });

    const res = await http.get("/trade-requests", { params: cleanParams });

    const data = res.data || {};
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number.isFinite(Number(data.total)) ? Number(data.total) : 0,
      limit: Number.isFinite(Number(data.limit)) ? Number(data.limit) : cleanParams.limit,
      offset: Number.isFinite(Number(data.offset)) ? Number(data.offset) : cleanParams.offset,
    };
  } catch (error) {
    console.error("Error fetching trade requests:", error);
    throw new Error(`Failed to fetch trade requests: ${errorMessage(error)}`);
  }
}

/**
 * Get completed trades (settled trades) for the current user.
 * Backend: GET /trade-history?settled_only=true
 * Returns an ARRAY; we wrap it into a list-like shape for convenience.
 */
export async function getCompletedTrades(params = {}) {
  try {
    const cleanParams = sanitizeParams({
      limit: clampLimit(params.limit ?? 50, 200),
      offset: params.offset ?? 0,
      settled_only: true,
    });

    const res = await http.get("/trade-history", { params: cleanParams });

    const items = Array.isArray(res.data) ? res.data : [];
    return {
      items,
      total: items.length,
      limit: cleanParams.limit,
      offset: cleanParams.offset,
    };
  } catch (error) {
    console.error("Error fetching completed trades:", error);
    throw new Error(`Failed to fetch completed trades: ${errorMessage(error)}`);
  }
}

/**
 * User Trade History (paged shape for UI)
 * Backend: GET /trade-history
 * Returns ARRAY; we provide {items,total,limit,offset,hasMore}
 * NOTE: total is best-effort without a backend count endpoint.
 */
export async function getMyTradeHistoryPaged(params = {}) {
  const limit = clampLimit(params.limit ?? 50, 200);
  const offset = Number(params.offset ?? 0) || 0;
  const settled_only = params.settled_only ?? params.settledOnly ?? false;

  try {
    const cleanParams = sanitizeParams({
      limit,
      offset,
      settled_only,
    });

    const res = await http.get("/trade-history", { params: cleanParams });
    const items = Array.isArray(res.data) ? res.data : [];

    return {
      items,
      total: offset + items.length,
      limit,
      offset,
      hasMore: items.length === limit,
    };
  } catch (error) {
    console.error("Error fetching my trade history:", error);
    throw new Error(`Failed to fetch trade history: ${errorMessage(error)}`);
  }
}

/**
 * ✅ Admin trade history (across users)
 * Backend: GET /admin/trade-history
 * Returns PAGED OBJECT: { items, total, limit, offset }
 */
export async function getAdminTradeHistory(params = {}) {
  try {
    // Backend router: limit <= 200
    const ADMIN_MAX_LIMIT = 200;

    const cleanParams = sanitizeParams({
      user_id: params.user_id ?? params.userId ?? undefined,
      outcome: params.outcome ?? undefined,
      status: params.status ?? undefined,
      market: params.market ?? undefined,
      direction: params.direction ?? undefined,
      // backend expects settled_only boolean
      settled_only: params.settled_only ?? params.settledOnly ?? undefined,
      limit: clampLimit(params.limit ?? 50, ADMIN_MAX_LIMIT),
      offset: params.offset ?? 0,
    });

    const res = await http.get("/admin/trade-history", { params: cleanParams });

    const data = res.data || {};
    const items = Array.isArray(data.items) ? data.items : [];

    return {
      items,
      total: Number.isFinite(Number(data.total)) ? Number(data.total) : 0,
      limit: Number.isFinite(Number(data.limit)) ? Number(data.limit) : cleanParams.limit,
      offset: Number.isFinite(Number(data.offset)) ? Number(data.offset) : cleanParams.offset,
    };
  } catch (error) {
    console.error("Error fetching admin trade history:", error);
    throw new Error(`Failed to fetch admin trade history: ${errorMessage(error)}`);
  }
}

/**
 * Completed trades summary
 * Client-computed using /trade-history?settled_only=true
 */
export async function getCompletedTradesSummary(params = {}) {
  try {
    const cleanParams = sanitizeParams({
      limit: clampLimit(params.limit ?? 200, 200),
      offset: 0,
      settled_only: true,
    });

    const res = await http.get("/trade-history", { params: cleanParams });
    const items = Array.isArray(res.data) ? res.data : [];

    let wins = 0;
    let losses = 0;
    let pushes = 0;
    let totalStake = 0;
    let totalPayout = 0;

    for (const t of items) {
      const outcome = String(t?.outcome || "").toUpperCase();
      if (outcome === "WIN") wins += 1;
      else if (outcome === "LOSE") losses += 1;
      else if (outcome === "PUSH") pushes += 1;

      totalStake += Number(t?.stake_amount || 0);
      totalPayout += Number(t?.payout_amount || 0);
    }

    return {
      totalSettled: items.length,
      wins,
      losses,
      pushes,
      totalStake: Number(totalStake || 0),
      totalPayout: Number(totalPayout || 0),
    };
  } catch (error) {
    console.error("Error fetching completed trades summary:", error);
    return {
      totalSettled: 0,
      wins: 0,
      losses: 0,
      pushes: 0,
      totalStake: 0,
      totalPayout: 0,
    };
  }
}

/**
 * Get trade history (StatisticsPage.jsx calls this)
 * Backend: GET /trade-history
 * Returns: array of TradeRequest-like rows
 */
export async function getTradeHistory(params = {}) {
  try {
    const cleanParams = sanitizeParams({
      limit: clampLimit(params.limit ?? 50, 200),
      offset: params.offset ?? 0,
      settled_only: params.settled_only ?? params.settledOnly ?? false,
    });

    const res = await http.get("/trade-history", { params: cleanParams });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Error fetching trade history:", error);
    throw new Error(`Failed to fetch trade history: ${errorMessage(error)}`);
  }
}

/**
 * Get trade statistics
 * Backend: GET /trade/stats
 */
export async function getTradeStatistics(params = {}) {
  try {
    const cleanParams = sanitizeParams(params);
    const res = await http.get("/trade/stats", { params: cleanParams });

    const data = res.data || {};
    return {
      totalSettled: Number(data.totalSettled || 0),
      wins: Number(data.wins || 0),
      losses: Number(data.losses || 0),
      pushes: Number(data.pushes || 0),
      winRate: Number(data.winRate || 0),
      totalStake: Number(data.totalStake || 0),
      totalPayout: Number(data.totalPayout || 0),
    };
  } catch (error) {
    console.error("Error fetching trade statistics:", error);
    throw new Error(`Failed to fetch trade statistics: ${errorMessage(error)}`);
  }
}

/**
 * Get performance metrics by time period
 * Soft-fallback if endpoint isn't implemented.
 */
export async function getTradePerformance(period = "30d") {
  try {
    const res = await http.get("/trade/performance", { params: { period } });
    return res.data;
  } catch (error) {
    console.error("Error fetching trade performance:", error);
    return null;
  }
}

/**
 * Update trade status (admin/mod only)
 * Backend: PATCH /trade-requests/:id
 */
export async function updateTradeStatus(tradeId, status, reason = "") {
  try {
    const res = await http.patch(`/trade-requests/${tradeId}`, { status, reason });
    return res.data;
  } catch (error) {
    console.error(`Error updating trade ${tradeId}:`, error);
    throw new Error(`Failed to update trade status: ${errorMessage(error)}`);
  }
}

/**
 * Cancel a pending trade request
 * Backend: DELETE /trade-requests/:id
 */
export async function cancelTradeRequest(tradeId) {
  try {
    const res = await http.delete(`/trade-requests/${tradeId}`);
    return res.data;
  } catch (error) {
    console.error(`Error cancelling trade ${tradeId}:`, error);
    throw new Error(`Failed to cancel trade: ${errorMessage(error)}`);
  }
}

/**
 * Get trade details by ID
 * Backend: GET /trade-requests/:id
 */
export async function getTradeDetails(tradeId) {
  try {
    const res = await http.get(`/trade-requests/${tradeId}`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching trade details ${tradeId}:`, error);
    throw new Error(`Failed to fetch trade details: ${errorMessage(error)}`);
  }
}

export default {
  createTradeRequest,
  getTradeRequests,
  getCompletedTrades,
  getMyTradeHistoryPaged,
  getAdminTradeHistory,
  getCompletedTradesSummary,
  getTradeHistory,
  getTradeStatistics,
  getTradePerformance,
  updateTradeStatus,
  cancelTradeRequest,
  getTradeDetails,
};
